<template>

	<!-- Creator -->
	<div class="form-group">
		<label for="creatorId" class="form-label">
			Creator<br/>
			<small class="text-muted">Must be a creator contracted for this project</small>
		</label>

		<select v-model="creatorId"
			:class="'form-select mb-3' + (hasError('creator_id') ? ' is-invalid' : '')">
			<option disabled value="">Select a Creator</option>
			<option v-for="(creator, index) in projectCreators" :key="creator.id" :value="creator.id">{{ creator.name }}</option>
		</select>

		<div v-if="hasError('creator_id')" class="invalid-feedback">{{ errors.creator_id[0] }}</div>
	</div>

	<hr>

	<div class="form-group">
		<label for="creativeAssetId" class="form-label">
			Source Asset<br/>
			<small class="text-muted">Select the approved creative asset this ad is based on</small>
		</label>

		<div v-if="this.creatorId == null" :class="'card' + (hasError('creative_asset_id') ? ' is-invalid border-danger mb-2' : '')">
			<div class="card-body text-muted">
				Select a creator above to view their approved creative assets.
			</div>
		</div>
		<div v-else-if="this.creatorId != null && !creativeAssets.length" :class="'card' + (hasError('creative_asset_id') ? ' is-invalid border-danger mb-2' : '')">
			<div class="card-body text-muted">
				That creator doesn't have any approved creative assets!
			</div>
		</div>
		<div v-else style="max-height:300px;" :class="'card overflow-auto' + (hasError('creative_asset_id') ? ' is-invalid border-danger mb-2' : '')">

			<div v-for="(creativeAsset, index) in creativeAssets" :key="creativeAsset.id">
				<label class="list-group-item list-group-item-action">
					<div class="row align-items-center">
						<div class="col-2">

							<div v-if="creativeAsset.latest_version.aggregate_type == 'image' && creativeAsset.latest_version.embeddable"
								class="bg-secondary ratio ratio-1x1">
								<img :src="creativeAsset.latest_version.embeddable.url" style="object-fit: cover;">
							</div>

							<div v-else-if="creativeAsset.latest_version.aggregate_type == 'video' && creativeAsset.latest_version.embeddable && creativeAsset.latest_version.poster"
								class="bg-secondary ratio ratio-1x1">
								<img :src="creativeAsset.latest_version.poster.url" style="object-fit: cover;">
							</div>

							<div v-else class="bg-secondary ratio ratio-1x1 text-center">
								<div class="d-flex flex-column justify-content-center align-items-center">
									<div class="text-white">
										<i class="fe fe-alert-circle"></i>
									</div>
								</div>
							</div>
						</div>
						<div class="col-9">
							<i v-if="creativeAsset.latest_version.aggregate_type == 'video'" class="fe fe-film ms-1"></i>
							<i v-if="creativeAsset.latest_version.aggregate_type == 'image'" class="fe fe-image ms-1"></i>
							<span class="fw-bold">
								{{ creativeAsset.latest_version.original_name }}
							</span>
							<small class="badge text-secondary bg-light ms-1 mt-n1">
								v{{ creativeAsset.latest_version.version }}
							</small>
							<div class="small text-muted" v-if="creativeAsset.deliverable != null">
								{{ creativeAsset.creator.name }} | {{ creativeAsset.deliverable.name }}
							</div>
						</div><!--/.col-->
						<div class="col-1">
							<input
								class="form-check-input"
								type="radio"
								id="creativeAssetId"
								v-model="creativeAssetId"
								:value="creativeAsset.id"
								@change="setAdHeadlineAndCaption(creativeAsset)" />
						</div>
					</div><!--/.row-->
				</label><!--/.list-group-item-->
			</div><!--/v-for creative assets-->

		</div><!--/.card-->

		<div v-if="hasError('creative_asset_id')" class="invalid-feedback">
			{{ errors.creative_asset_id[0] }}
		</div>
	</div>

	<hr>

	<div class="form-group">
		<label for="name" class="form-label">
			Ad Name<br/>
			<small class="text-muted">Ad name should only contain alphanumeric characters, and no spaces.</small>
		</label>

		<input v-model="name"
			type="text"
			:class="'form-control' + (hasError('name') ? ' is-invalid' : '')">

		<div v-if="hasError('name')" class="invalid-feedback">{{ errors.name[0] }}</div>
	</div>

	<hr>

	<!-- Ad Type -->
	<div class="form-group">
		<label for="type" class="form-label">
			Ad Type
		</label>

		<select v-model="type"
			:class="'form-select mb-3' + (hasError('type') ? ' is-invalid' : '')">
			<option disabled value="">Select Ad Type</option>
			<option value="facebook_image">Facebook Image</option>
			<option value="facebook_video">Facebook Video</option>
			<option value="tiktok_image">TikTok Image</option>
			<option value="tiktok_video">TikTok Video</option>
		</select>

		<div v-if="hasError('type')" class="invalid-feedback">{{ errors.type[0] }}</div>
	</div>

	<hr>

	<div class="form-group">
		<label for="headline" class="form-label">
			Headline<br/>
			<small class="text-muted">Max 40 characters</small>
		</label>

		<input v-model="headline" type="text"
			:class="'form-control' + (hasError('headline') ? ' is-invalid' : '')">

		<div v-if="hasError('headline')" class="invalid-feedback">
			{{ errors.headline[0] }}
		</div>
	</div>

	<hr>

	<div class="form-group">
		<label for="caption" class="form-label">
			Caption
		</label>

		<textarea v-model="caption"
			rows="1" data-autosize
			:class="'form-control' + (hasError('caption') ? ' is-invalid' : '')"
		></textarea>

		<div v-if="hasError('caption')" class="invalid-feedback">
			{{ errors.caption[0] }}
		</div>
	</div>

	<hr>

	<div class="form-group">
		<label for="link_url" class="form-label">
			Link URL
		</label>

		<input v-model="linkUrl" type="text"
			:class="'form-control' + (hasError('link_url') ? ' is-invalid' : '')">

		<div v-if="hasError('link_url')" class="invalid-feedback">
			{{ errors.link_url[0] }}
		</div>
	</div>

	<hr>

	<div class="form-group">
		<label for="url_parameters" class="form-label">
			URL Parameters<br/>
			<small class="text-muted"
				v-html="'e.g. utm_source=facebook&utm_campaign={{campaign.name}}&utm_content={{ad.name}}'"
			></small>
		</label>

		<input v-model="urlParameters" type="text"
			:class="'form-control' + (hasError('url_parameters') ? ' is-invalid' : '')">

		<div v-if="hasError('url_parameters')" class="invalid-feedback">
			{{ errors.url_parameters[0] }}
		</div>
	</div>

	<hr>

	<label for="media" class="form-label">
		Upload Ad Media
	</label>

	<div :class="'card' + (hasError('media') ? ' border-danger is-invalid' : '')">
		<div class="card-body">
			<uppy-drag-drop :uppy="uppy" class="mb-3"/>
			<div class="list-group list-group-flush">

				<div v-if="filesUploading.length || filesUploaded.length" class="list-group mt-3">

					<!-- Uploading/Uploaded Files -->
					<div v-for="(file, index) in filesUploaded" :key="file.id" class="list-group-item">
						<div class="row align-items-center">
							<div class="col">
								<span class="fw-bold">{{ file.name }}</span>
								<span class="badge bg-success ms-2">Complete</span><br/>
								<small class="text-muted">{{ formatBytes(file.size) }}</small>
							</div>
							<div class="col-auto">
								<button class="btn btn-sm btn-rounded-circle btn-white" @click="deleteUpload(file.s3Multipart.key, file.s3Multipart.uploadId)">
									<span class="fe fe-x"></span>
								</button>
							</div>
						</div><!--/.row-->
					</div><!--/.list-group-item-->

					<div v-for="(file, index) in filesUploading" :key="file.id" class="list-group-item">
						<div class="row">
							<div class="col">
								<span class="fw-bold">{{ file.name }}</span>

								<span class="badge bg-light ms-2">
									{{ Math.round(file.progress.percentage) }}%
								</span>
								<br/>

								<small class="text-muted">{{ formatBytes(file.size) }}</small>
							</div>
						</div><!--/.row-->
						<div class="progress progress-sm mt-3">
							<div class="progress-bar" role="progressbar"
								:style="'width: ' + file.progress.percentage + '%'"
								:aria-valuenow="file.progress.percentage"
								aria-valuemin="0" aria-valuemax="100"></div>
						</div>
					</div><!--/.list-group-item-->

				</div><!--/.list-group-->


				<!-- Existing Media -->
				<div v-if="this.ad && this.ad.media.length">
					<div v-for="(media, index) in this.ad.media" :key="media.id" class="list-group-item">
						<div class="row align-items-center">
							<div class="col">
								<h4 class="mb-1">{{ media.original_name }}</h4>
								<small class="text-muted">{{ formatBytes(media.size) }}</small>
							</div>
							<div class="col-auto">
								<button class="btn btn-sm btn-rounded-circle btn-white" @click="deleteMedia(media.id)">
									<span class="fe fe-x"></span>
								</button>
							</div>
						</div><!--/.row-->
					</div>
				</div>

			</div>
		</div>
	</div>

	<div v-if="hasError('media')" class="invalid-feedback">{{ errors.media[0] }}</div>

	<hr class="mb-5">

	<button :class="buttonClasses" @click.prevent="submit">
		<div v-if="this.isSubmitting"
			class="spinner-border text-white"
			role="status">
			<span class="visually-hidden">Loading...</span>
		</div>
		<div v-else>{{ buttonText }}</div>
	</button>
	<a class="btn btn-lg w-100 btn-link text-muted" :href="redirect">Cancel</a>

</template>


<script>
	import Uppy from '@uppy/core'
	import AwsS3Multipart from '@uppy/aws-s3-multipart'
	import { DragDrop } from '@uppy/vue';
	import autosize from 'autosize';
	import '@uppy/core/dist/style.css'
	import '@uppy/drag-drop/dist/style.min.css';
	import '@uppy/progress-bar/dist/style.min.css';

	export default {

		props: [
			'adModel',
			'projectCreators',
			'projectApprovedCreativeAssets',
			'endpoint',
			'redirect',
			'buttonText'
		],

		components: {
			'uppy-drag-drop': DragDrop,
		},

		data() {
			return {
				ad: this.adModel ?? null,

				creatorId: this.adModel ? this.adModel.creator_id : null,
				name: this.adModel ? this.adModel.name : null,
				type: this.adModel ? this.adModel.type : null,
				creativeAssetId: this.adModel ? this.adModel.creative_asset_id : null,
				headline: this.adModel ? this.adModel.headline : null,
				caption: this.adModel ? this.adModel.caption : null,
				linkUrl: this.adModel ? this.adModel.link_url : null,
				urlParameters: this.adModel ? this.adModel.url_parameters : null,

				// files: [],
				// media: [],
				filesUploading: [],
				filesUploaded: [],

				errors: null,
				isSubmitting: false,
			}
		},

		mounted() {

			/*
			Initialize autosize textareas
			=================================*/
			const autosizeTextareas = document.querySelectorAll('[data-autosize]');

			autosizeTextareas.forEach((autosizeTextarea) => {
				autosize(autosizeTextarea);
			});

			/*
			Register Uppy event listeners
			=================================*/
			this.uppy.on('file-added', (file) => {
				// add file to filesUploading array
				this.filesUploading.push(file);
			});

			this.uppy.on('upload', (data) => {
				// console.log('upload', data);
			});

			this.uppy.on('upload-progress', (file, progress) => {
				for (var i = 0; i < this.filesUploading.length; i++) {
					if(this.filesUploading[i].id == file.id) {
						this.filesUploading[i].progress.percentage = progress.bytesUploaded / progress.bytesTotal * 100;
					}
				}
			});

			this.uppy.on('upload-success', (file, response) => {
				this.handleUploadSuccess(file, response);
			});

			this.uppy.on('complete', (result) => {
				// console.log('complete');
				// console.log('successful files:', result.successful);
				// console.log('failed files:', result.failed);
			});

			this.uppy.on('error', (error) => {
				console.error(error);
			});

			this.uppy.on('upload-error', (file, error, response) => {
				console.log('upload-error');
				console.log('error with file:', file.id);
				console.log('error message:', error);
			});

		},

		computed: {

			uppy() {
				// initialize Uppy
				const uppy = new Uppy({
					// https://uppy.io/docs/uppy/
					id: 'uppy',
					autoProceed: true,
					allowMultipleUploadBatches: true,
					debug: true,
					inline: true,
					meta: {
						// deliverableId: this.deliverable.id
					},
					restrictions: {
						allowedFileTypes: this.allowedFileTypes,
						maxFileSize: 1000 * 1000 * 1000 * 1000 * 2, /* 2 GB */
						maxNumberOfFiles: 20,
						minNumberOfFiles: 1,
					}
				}).use(AwsS3Multipart, {
					limit: 5,
					companionUrl: '/',
					companionHeaders:
					{
						'X-CSRF-TOKEN': window.csrfToken,
					},
				});

				return uppy;
			},

			allowedFileTypes() {
				return ['image/*', 'video/*', 'audio/*'];
			},

			buttonClasses() {

				var classes = 'btn btn-lg w-100 mb-3 ';

				// if(this.isSubmitting) {
				// 	return classes + 'btn-primary disabled';
				// }
				// else if(this.filesUploaded.length > 0 || (this.ad && this.ad.media.length > 0)) {
				// 	return classes + 'btn-primary';
				// }
				// else {
				// 	return classes + 'btn-secondary disabled';
				// }

				if(this.isSubmitting) {
					return classes + 'btn-primary disabled';
				}
				else {
					return classes + 'btn-primary';
				}
			},

			creativeAssets() {

				var creativeAssets = [];

				for(var x = 0; x < this.projectApprovedCreativeAssets.length; x++)
				{
					if(this.projectApprovedCreativeAssets[x].creator_id == this.creatorId)
					{
						creativeAssets.push(this.projectApprovedCreativeAssets[x]);
					}
				}
				console.log(creativeAssets);

				return creativeAssets;
			},

		},

		methods: {

			submit() {
				this.isSubmitting = true;

				var postData = {
					creator_id: this.creatorId,
					name: this.name,
					type: this.type,
					creative_asset_id: this.creativeAssetId,
					headline: this.headline,
					caption: this.caption,
					link_url: this.linkUrl,
					url_parameters: this.urlParameters,
					media: this.filesUploaded,
				};

				console.log(this.filesUploaded);

				if(this.adModel) {
					axios.patch(this.endpoint, postData)
						.then((response) => {
							console.log('response', response);
							location.href = this.redirect;
						})
						.catch((e) => {
							this.isSubmitting = false;
							console.log('errors', e.response.data.errors);
							this.errors = e.response.data.errors;
						});
				}
				else {
					axios.post(this.endpoint, postData)
						.then((response) => {
							console.log('response', response);
							location.href = this.redirect;
						})
						.catch((e) => {
							this.isSubmitting = false;
							console.log('errors', e.response.data.errors);
							this.errors = e.response.data.errors;
						});
				}

			},

			setAdHeadlineAndCaption(creativeAsset) {
				this.headline = creativeAsset.title;
				this.caption = creativeAsset.caption;

				/*
				Update autosize textareas
				=================================*/
				const autosizeTextareas = document.querySelectorAll('[data-autosize]');

				autosizeTextareas.forEach((autosizeTextarea) => {
					autosize.update(autosizeTextarea);
				});
			},

			handleUploadSuccess(file, response) {

				// remove from filesUploading array
				for (var i = 0; i < this.filesUploading.length; i++)
				{
					if(this.filesUploading[i].id == file.id) {
						this.filesUploading.splice(i, 1);
					}
				}

				// add to filesUploaded array
				this.filesUploaded.push(file);
			},

			hasError(field) {
				if(this.errors && typeof this.errors[field] !== 'undefined') {
					return true;
				}
				return false;
			},

			deleteUpload(key, uploadId) {
				axios.delete('/s3/multipart/' + uploadId + '?key=' + key)
					.then((response) => {
						// remove from filesUploaded array
						for (var i = 0; i < this.filesUploaded.length; i++)
						{
							if(this.filesUploaded[i].s3Multipart.key == key) {
								this.filesUploaded.splice(i, 1);
							}
						}
					})
					.catch((error) => {
						console.error(error);
					});
			},

			deleteMedia(mediaId) {
				axios.delete('/admin/projects/' + this.ad.project_id + '/ads/' + this.ad.id + '/media/' + mediaId)
					.then((response) => {
						// remove from this.ad.media array
						for (var i = 0; i < this.ad.media.length; i++)
						{
							if(this.ad.media[i].id == mediaId) {
								this.ad.media.splice(i, 1);
							}
						}
					})
					.catch((error) => {
						console.error(error);
					});
			},

			isLowRes(width, height) {
				if(width > 0 && height > 0 && (width * height < 1920 * 1080)) {
					return true;
				}
				else {
					return false;
				}
			},

			formatBytes(bytes, decimals = 0) {
				if (bytes === 0) return '0 bytes';
				const k = 1024;
				const dm = decimals < 0 ? 0 : decimals;
				const sizes = ['bytes', 'kb', 'mb', 'gb', 'tb', 'pb', 'eb', 'zb', 'yb'];
				const i = Math.floor(Math.log(bytes) / Math.log(k));
				return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
			},

			nl2br(str, is_xhtml) {
				var breakTag = (is_xhtml || typeof is_xhtml === 'undefined') ? '<br />'   : '<br>';
				return (str + '').replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, '$1' + breakTag   + '$2');
			}
		},

		beforeDestroy () {
			uppy.close()
		}
	}

</script>
